import type { Editor } from '@tiptap/react';
import { TextSelection, type Transaction } from 'prosemirror-state';
import { canSplit, findWrapping, liftTarget } from 'prosemirror-transform';

/**
 * Split textblocks at the selection edges so a partial highlight becomes its
 * own paragraph(s). Block-level commands then apply only to that slice.
 * Returns true when the transaction was changed.
 */
export function isolateSelectionRange(tr: Transaction): boolean {
  const sel = tr.selection;
  if (sel.empty || !(sel instanceof TextSelection)) return false;
  const $from = sel.$from;
  const $to = sel.$to;
  if (!$from.parent.isTextblock || !$to.parent.isTextblock) return false;

  const fromBlockStart = $from.start();
  const toBlockEnd = $to.end();
  const needSplitStart = sel.from > fromBlockStart;
  const needSplitEnd = sel.to < toBlockEnd;

  if (!needSplitStart && !needSplitEnd) {
    if (sel.from === fromBlockStart && sel.to === toBlockEnd) return false;
    tr.setSelection(TextSelection.create(tr.doc, fromBlockStart, toBlockEnd));
    return true;
  }

  // Exclusive `to` maps past a split. Keep the last selected character so the
  // range stays in the isolated slice, not the leftover tail.
  const lastContent = Math.max(sel.from, sel.to - 1);

  if (needSplitEnd && canSplit(tr.doc, sel.to)) {
    tr.split(sel.to);
  }
  if (needSplitStart) {
    const mappedFrom = tr.mapping.map(sel.from);
    if (canSplit(tr.doc, mappedFrom)) {
      tr.split(mappedFrom);
    }
  }

  const mappedFrom = tr.mapping.map(sel.from);
  const mappedLast = tr.mapping.map(lastContent);
  const start = tr.doc.resolve(mappedFrom).start();
  const end = tr.doc.resolve(mappedLast).end();
  tr.setSelection(TextSelection.create(tr.doc, start, end));
  return true;
}

/** Isolate a partial highlight, then run a block-level toggle on that slice. */
export function toggleBlockFormatOnSelection(editor: Editor, toggle: () => void): void {
  const { state, view } = editor;
  const tr = state.tr;
  if (isolateSelectionRange(tr)) {
    view.dispatch(tr);
  }
  toggle();
}

export function insertOrToggleBlockquote(editor: Editor | null): void {
  if (!editor) return;
  const { state, view } = editor;
  const blockquote = state.schema.nodes.blockquote;
  if (!blockquote) return;
  const tr = state.tr.setSelection(state.selection);
  isolateSelectionRange(tr);
  const range = tr.selection.$from.blockRange(tr.selection.$to);
  if (range) {
    if (range.parent.type === blockquote) {
      const target = liftTarget(range);
      if (target != null) tr.lift(range, target);
    } else {
      const wrapping = findWrapping(range, blockquote);
      if (wrapping) tr.wrap(range, wrapping);
    }
  }
  if (tr.docChanged || tr.selectionSet) {
    view.dispatch(tr);
  }
  editor.commands.focus();
}

export function insertOrToggleCodeBlock(editor: Editor | null): void {
  if (!editor) return;
  toggleBlockFormatOnSelection(editor, () => {
    editor.chain().focus().toggleCodeBlock().run();
  });
}
